"use client"

import * as React from "react"
import { MessageCircle } from "lucide-react"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider"
import { useDraggableFab } from "@/features/assistant/hooks/use-draggable-fab"
import { useLocale, useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { AnalyticsEvents, captureEvent } from "@/shared/lib/analytics/analytics"
import { useFeatureAccess } from "@/shared/feature-flags"
import { cn } from "@/shared/lib/utils"

/** FAB diameter in px (must match the `size-14` class below) — used to clamp drag within the viewport. */
const FAB_SIZE = 56

// Code-split: the panel pulls in the Vercel AI SDK (~heavy). Loading it lazily on first open keeps it out of
// the initial app bundle.
const AssistantPanel = React.lazy(() =>
  import("@/features/assistant/components/assistant-panel").then((module) => ({ default: module.AssistantPanel })),
)

/**
 * The floating "Ask Nuru" launcher (bottom-right). Mounted globally; only renders for an authenticated user
 * inside a workspace context, so it never appears pre-login. The panel is keyed by workspace so switching
 * workspace resets to that workspace's (DB-backed) conversation history.
 */
export function AssistantLauncher() {
  const t = useTranslations("assistant")
  const locale = useLocale()
  const { authMe, isAuthenticated } = useTenantAuthFlow()
  const workspaceSlug = useActiveWorkspaceSlug()
  const branchSlug = useActiveBranchSlug()
  const [open, setOpen] = React.useState(false)
  // Mount the (lazy) panel only once the assistant has been opened, then keep it mounted so the conversation
  // runtime persists across open/close.
  const [hasOpened, setHasOpened] = React.useState(false)
  const isMobile = useIsMobile()
  // On mobile the FAB can be dragged anywhere and remembers where it's dropped; desktop keeps it pinned.
  const { style: fabStyle, isDragging, suppressClickRef, handlers } = useDraggableFab({
    enabled: isMobile,
    size: FAB_SIZE,
  })
  // Rafiki assistant flag (per-workspace + global kill). Hidden entirely when off.
  const rafikiAllowed = useFeatureAccess("rafiki.use").allowed

  if (!isAuthenticated || !authMe || !workspaceSlug || !rafikiAllowed) return null

  const toggle = () => {
    // A finished drag fires a trailing click — ignore it so repositioning never opens the panel.
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    setHasOpened(true)
    // Count only opens (not closes) — the engagement entry point.
    if (!open) captureEvent(AnalyticsEvents.RafikiOpened, { locale })
    setOpen((value) => !value)
  }

  return (
    <>
      {hasOpened ? (
        <React.Suspense fallback={null}>
          <AssistantPanel
            key={workspaceSlug}
            open={open}
            onClose={() => setOpen(false)}
            workspaceSlug={workspaceSlug}
            branchSlug={branchSlug ?? null}
            locale={locale}
          />
        </React.Suspense>
      ) : null}

      <button
        type="button"
        data-tour='rafiki'
        onClick={toggle}
        {...handlers}
        style={fabStyle}
        aria-label={t("launcher")}
        aria-expanded={open}
        className={cn(
          // A round message-icon button (FAB) that opens the Rafiki assistant. Rafiki's photo lives inside the
          // open panel, not on this launcher.
          // Sit above the mobile bottom nav so it never covers a tab. Reuses the app's published nav-offset
          // var (same pattern as tenant-chrome-header); drops to the normal offset on md+ (no bottom nav).
          "bg-primary text-primary-foreground fixed right-4 bottom-[var(--tenant-mobile-bottom-nav-offset,1.5rem)] z-50 inline-flex size-14 items-center justify-center rounded-full shadow-[var(--shadow-floating)] ring-1 ring-black/10 transition-all duration-200 ease-out hover:-translate-y-0.5 active:scale-95 md:bottom-4 dark:ring-white/15",
          // Draggable on mobile: stop touch-scrolling while panning, and drop the position transition so it
          // tracks the finger 1:1 (the transition is only for the hover/press polish).
          isMobile && "touch-none",
          isDragging && "transition-none cursor-grabbing duration-0",
          // While the (near-fullscreen) panel is open on mobile/tablet, hide the FAB — the panel has its own
          // close button, so the floating button would just sit on top of the conversation.
          open && "max-md:hidden",
        )}
      >
        <MessageCircle className="size-6" aria-hidden="true" />
      </button>
    </>
  )
}
